import React from 'react';

import {
  DSI,
  asDsiMessage,
  postToFrame,
  previewOrigin,
  type DsiRect,
  type DsiSourceInfo,
  type DsiTarget,
} from '../live-preview';
import { gameTileOf, isListGap, listByContent, listFromContainers, listFromTarget } from '../preview-game-lists';
import {
  asSourceContent,
  gameKey,
  gameListsFromSources,
  gamesOf,
  orderedGames,
  pinnedContent,
  orderHolds,
  type SiteGame,
  type SiteSourceContent,
} from '../site-games';

/** How long a bridge request may take before it counts as unanswered. */
const REQUEST_TIMEOUT_MS = 5000;
/** How long to wait for the bridge to report a list's rect before settling for the hovered tile's. */
const LIST_RECT_TIMEOUT_MS = 500;
/** How long a hover-out waits before taking the overlay down. The bridge emits hover-out for the leaving
 *  tile, then hover for the next one, so a move between two tiles of the SAME list would otherwise strobe
 *  the list-wide overlay; a hover arriving within this window cancels the clear. */
const HOVER_OUT_GRACE_MS = 60;

export type PreviewGameListsOptions = {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  /** The framed page's URL (its origin is the only one trusted). */
  frameSrc: string;
  /** Draft orders by list, owned by the caller (the Game Lists tab), so the tab and the preview share them. */
  drafts: Record<string, string[]>;
  /** A list's draft changed (its new order) or was dropped (null: back to the site's own order). */
  onDraftChange: (dsId: string, ids: string[] | null) => void;
  /** List contents (the site's own order) the caller has already read. */
  contents: Record<string, SiteSourceContent>;
  /** A list's content read here, for the caller to keep. */
  onContent: (dsId: string, content: SiteSourceContent) => void;
};

/** A hovered / clicked game (or the list around it) resolved to its list. `whole`: the rect is the list's. */
type GameListHit = { dsId: string; rect: DsiRect; nodeId: string; whole: boolean };

/** The overlay over a whole game list: which list, and its rect (followed through `dsi-rects` under `nodeId`). */
export type ListOverlay = { dsId: string; nodeId: string; rect: DsiRect };

/** The list open in the reorder sheet. */
export type OpenGameList = {
  dsId: string;
  /** In draft order; null while the list is being read. */
  games: SiteGame[] | null;
  failed: boolean;
  dirty: boolean;
};

export type PreviewGameLists = {
  /** The framed page (re)connected or navigated: forget its lists, turn click capture off. */
  pageReady: () => void;
  /** The page's data sources as they load: pin each drafted list once it is there (once per page). */
  pinDrafts: (sources: DsiSourceInfo[]) => void;
  /** The "Reorder games" overlay over the list under the pointer. */
  overlay: ListOverlay | null;
  /** The hovered tile's rect, when its list's rect never arrived (the overlay then covers just the tile). */
  tileCue: { dsId: string; rect: DsiRect } | null;
  opened: OpenGameList | null;
  reorder: (ids: string[]) => void;
  reset: () => void;
  /** The sheet closed: apply its list's draft to the preview. */
  close: () => void;
};

type Waiters<T> = Map<string, Array<(value: T) => void>>;

/** Wait for a keyed bridge reply; resolves with `fallback` when none arrives in time. */
const waitFor = <T>(
  waiters: Waiters<T>,
  key: string,
  fallback: T,
  send: () => void,
  timeoutMs = REQUEST_TIMEOUT_MS,
): Promise<T> =>
  new Promise((resolve) => {
    let done = false;
    const finish = (value: T): void => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      resolve(value);
    };
    const timer = window.setTimeout(() => {
      const rest = (waiters.get(key) ?? []).filter((waiter) => waiter !== finish);
      if (rest.length) waiters.set(key, rest);
      else waiters.delete(key);
      finish(fallback);
    }, timeoutMs);
    const pending = waiters.get(key);
    waiters.set(key, [...(pending ?? []), finish]);
    if (!pending) send();
  });

const asSources = (value: unknown): DsiSourceInfo[] =>
  Array.isArray(value)
    ? value.filter((s): s is DsiSourceInfo => typeof s === 'object' && s !== null && typeof s.id === 'string')
    : [];

/**
 * Game lists in the Game Lists tab's live site preview: hovering one of a list's games (or the gaps between
 * them) lays a "Reorder games" overlay over the whole list; clicking it opens the list in the reorder sheet;
 * the drafts (the tab's own) are applied to the framed page over the dsi bridge. Preview only.
 *
 * The list under the pointer comes from the bridge's own container chain when the framed site sends one
 * (`listFromContainers`), and is worked out from the game's id and position when it does not — a site
 * deployed before that bridge change. Same for its rect: the chain carries the list's, otherwise it is
 * fetched by highlighting the list and clearing the highlight at once (the bridge then tracks and reports
 * it without drawing its own box).
 *
 * The overlay is PASSIVE (`pointer-events: none`): the pointer stays with the site, so the page scrolls
 * under it, the carousel's own arrows keep working, and hovers keep flowing. Clicking a game opens its
 * list through `dsi-click` — the site swallows that click itself while capture is on, so nothing launches.
 */
export const usePreviewGameLists = ({
  iframeRef,
  frameSrc,
  drafts,
  onDraftChange,
  contents,
  onContent,
}: PreviewGameListsOptions): PreviewGameLists => {
  const origin = previewOrigin(frameSrc);
  const sourcesRef = React.useRef<DsiSourceInfo[]>([]);
  const sourceWaitersRef = React.useRef<Waiters<DsiSourceInfo[]>>(new Map());
  const contentWaitersRef = React.useRef<Waiters<unknown>>(new Map());
  // List instance ids on the current page (`dsi-i13`) and the list each renders.
  const instancesRef = React.useRef(new Map<string, string>());
  // The latest rect `dsi-rects` reported per node on the current page, and who is waiting for one.
  const rectsRef = React.useRef(new Map<string, DsiRect>());
  const rectWaitersRef = React.useRef<Waiters<DsiRect | null>>(new Map());
  // The lists whose draft is pinned on the current page.
  const pinnedRef = React.useRef(new Set<string>());

  // The caller's drafts, contents and callback, readable from the bridge listener and async resolutions.
  const draftsRef = React.useRef(drafts);
  const contentsRef = React.useRef(contents);
  const onContentRef = React.useRef(onContent);
  React.useLayoutEffect(() => {
    draftsRef.current = drafts;
    contentsRef.current = contents;
    onContentRef.current = onContent;
  }, [drafts, contents, onContent]);

  const [overlay, setOverlay] = React.useState<ListOverlay | null>(null);
  const [tileCue, setTileCue] = React.useState<{ dsId: string; rect: DsiRect; nodeId: string } | null>(null);
  // The node the bridge reported the current game hover on (what its `dsi-hover-out` names).
  const hoverTargetRef = React.useRef<string | null>(null);
  // A pending hover-out overlay clear, cancelled if a new hover lands first (tile-to-tile in one list).
  const hoverOutTimerRef = React.useRef<number | undefined>(undefined);
  // Click capture: on while a game is under the pointer (a quick click must not launch it), off otherwise.
  const captureOnRef = React.useRef(false);

  const [openId, setOpenId] = React.useState<string | null>(null);
  const openIdRef = React.useRef(openId);
  React.useLayoutEffect(() => {
    openIdRef.current = openId;
  }, [openId]);
  const [openFailed, setOpenFailed] = React.useState(false);

  const post = React.useCallback(
    (type: string, payload?: Record<string, unknown>): void => {
      if (origin) postToFrame(iframeRef.current?.contentWindow, origin, type, payload);
    },
    [origin, iframeRef],
  );

  const applyCapture = React.useCallback(
    (enabled: boolean): void => {
      if (captureOnRef.current === enabled) return;
      captureOnRef.current = enabled;
      post(DSI.SET_CAPTURE, { enabled });
    },
    [post],
  );

  const fetchSources = React.useCallback(
    (): Promise<DsiSourceInfo[]> =>
      waitFor(sourceWaitersRef.current, 'sources', sourcesRef.current, () => post(DSI.LIST_SOURCES)),
    [post],
  );

  /** A list's own content: already known to the caller, or read from the page (and handed to the caller). */
  const fetchOriginal = React.useCallback(
    async (dsId: string): Promise<SiteSourceContent | null> => {
      const known = contentsRef.current[dsId];
      if (known) return known;
      const content = asSourceContent(
        await waitFor(contentWaitersRef.current, dsId, null, () => post(DSI.GET_SOURCE, { dsId })),
      );
      if (content) {
        contentsRef.current = { ...contentsRef.current, [dsId]: content };
        onContentRef.current(dsId, content);
      }
      return content;
    },
    [post],
  );

  /** What the page renders for a list right now: its draft over the original, or the original. */
  const renderedContent = React.useCallback(
    async (dsId: string): Promise<SiteSourceContent | null> => {
      const original = await fetchOriginal(dsId);
      const draft = draftsRef.current[dsId];
      return original && draft ? pinnedContent(original, draft) : original;
    },
    [fetchOriginal],
  );

  /** The page's game lists: as last reported, or asked for afresh (`refresh`, or none known yet). */
  const gameListIds = React.useCallback(
    async (refresh = false): Promise<Set<string>> => {
      const known = refresh ? [] : gameListsFromSources(sourcesRef.current);
      const lists = known.length ? known : gameListsFromSources(await fetchSources());
      return new Set(lists.map((list) => list.id));
    },
    [fetchSources],
  );

  const isCandidate = React.useCallback(
    (target: DsiTarget): boolean =>
      listFromContainers(target) !== null || gameTileOf(target) !== null || isListGap(target),
    [],
  );

  const resolve = React.useCallback(
    async (target: DsiTarget): Promise<GameListHit | null> => {
      // The bridge named the list (and gave its rect): nothing to work out.
      const named = listFromContainers(target);
      if (named) return { dsId: named.dsId, rect: named.rect, nodeId: named.nodeId, whole: true };
      if (isListGap(target)) {
        const dsId = listFromTarget(target, await gameListIds()) ?? listFromTarget(target, await gameListIds(true));
        return dsId ? { dsId, rect: target.rect, nodeId: target.nodeId, whole: true } : null;
      }
      const tile = gameTileOf(target);
      if (!tile) return null;
      const hit = (dsId: string): GameListHit => {
        if (tile.instance) instancesRef.current.set(tile.instance, dsId);
        return { dsId, rect: target.rect, nodeId: target.nodeId, whole: false };
      };
      const known = tile.instance ? instancesRef.current.get(tile.instance) : undefined;
      if (known) return hit(known);
      // Lists keep loading after the page connects: a game no known list explains asks for them afresh, once.
      for (const refresh of [false, true]) {
        const lists = await gameListIds(refresh);
        const direct = listFromTarget(target, lists);
        if (direct) return hit(direct);
        const found = new Map<string, SiteSourceContent>();
        await Promise.all(
          Array.from(lists).map(async (dsId) => {
            const content = await renderedContent(dsId);
            if (content) found.set(dsId, content);
          }),
        );
        const dsId = listByContent(tile, found, instancesRef.current);
        if (dsId) return hit(dsId);
      }
      return null;
    },
    [gameListIds, renderedContent],
  );

  /**
   * Today's bridge reports a list's rect only for the gaps between its tiles, but it tracks (and reports) any
   * node it is asked to highlight, a list included (looked up by its dsId). Highlighting and at once clearing
   * it gets the rect reported without the site drawing its own highlight box, and keeps the list tracked, so
   * its rect follows scrolling. A list still tracked with an unchanged rect is not reported again: the last
   * reported rect stands in.
   */
  const measureList = React.useCallback(
    (dsId: string): Promise<DsiRect | null> => {
      post(DSI.HIGHLIGHT, { nodeId: dsId });
      post(DSI.HIGHLIGHT, { nodeId: null });
      const known = rectsRef.current.get(dsId);
      if (known) return Promise.resolve(known);
      return waitFor(rectWaitersRef.current, dsId, null, () => undefined, LIST_RECT_TIMEOUT_MS);
    },
    [post],
  );

  const applyDraft = React.useCallback(
    (dsId: string): void => {
      const original = contentsRef.current[dsId];
      const draft = draftsRef.current[dsId];
      if (!original || !draft) return;
      post(DSI.SET_SOURCE, { dsId, content: pinnedContent(original, draft) });
      pinnedRef.current.add(dsId);
    },
    [post],
  );

  const open = React.useCallback(
    (dsId: string): void => {
      setOpenId(dsId);
      setOpenFailed(false);
      void fetchOriginal(dsId).then((content) => {
        if (openIdRef.current === dsId && !content) setOpenFailed(true);
      });
    },
    [fetchOriginal],
  );

  const clearHover = React.useCallback((): void => {
    hoverTargetRef.current = null;
    setTileCue(null);
  }, []);

  // Syncs with the framed site: replies to our requests, the pointer over its game lists, and list rects.
  React.useEffect(() => {
    if (!origin) return undefined;

    // A game (or a gap between a list's games) under the pointer: capture at once, resolve its list, and lay
    // the overlay over the whole list once its rect is known (over the tile when it never is). Dropped if the
    // pointer moves on meanwhile.
    const onGameHover = (target: DsiTarget): void => {
      window.clearTimeout(hoverOutTimerRef.current);
      hoverOutTimerRef.current = undefined;
      hoverTargetRef.current = target.nodeId;
      applyCapture(true);
      void (async () => {
        const hit = await resolve(target);
        if (hoverTargetRef.current !== target.nodeId) return;
        if (!hit) {
          // Not a list: drop any overlay a previous list left, and let the site navigate again.
          setOverlay(null);
          clearHover();
          applyCapture(false);
          return;
        }
        const rect = hit.whole ? hit.rect : await measureList(hit.dsId);
        if (hoverTargetRef.current !== target.nodeId) return;
        if (rect) {
          // Keep hoverTargetRef on THIS leaf (do not clearHover, which nulls it) so the leaf's own
          // dsi-hover-out matches below and takes the overlay down; only drop a stale tile cue.
          setTileCue(null);
          setOverlay({ dsId: hit.dsId, nodeId: hit.whole ? hit.nodeId : hit.dsId, rect });
        } else {
          // Only a tile cue this time: a prior list overlay must not stay painted over it.
          setOverlay(null);
          setTileCue({ dsId: hit.dsId, rect: hit.rect, nodeId: hit.nodeId });
        }
      })();
    };

    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== origin || event.source !== iframeRef.current?.contentWindow) return;
      const msg = asDsiMessage(event.data);
      if (!msg) return;
      if (msg.type === DSI.SOURCES) {
        const sources = asSources(msg.payload.sources);
        sourcesRef.current = sources;
        const waiting = sourceWaitersRef.current.get('sources');
        sourceWaitersRef.current.delete('sources');
        waiting?.forEach((finish) => finish(sources));
      } else if (msg.type === DSI.SOURCE) {
        const dsId = typeof msg.payload.dsId === 'string' ? msg.payload.dsId : '';
        const waiting = contentWaitersRef.current.get(dsId);
        contentWaitersRef.current.delete(dsId);
        waiting?.forEach((finish) => finish(msg.payload.content ?? null));
      } else if (msg.type === DSI.RECTS) {
        const rects = msg.payload.rects;
        if (typeof rects !== 'object' || rects === null) return;
        const byNode = rects as Record<string, DsiRect>;
        for (const [nodeId, rect] of Object.entries(byNode)) {
          rectsRef.current.set(nodeId, rect);
          const waiting = rectWaitersRef.current.get(nodeId);
          rectWaitersRef.current.delete(nodeId);
          waiting?.forEach((finish) => finish(rect));
        }
        // Follow the overlaid list and the tile cue as the site scrolls.
        setOverlay((current) =>
          current && byNode[current.nodeId] ? { ...current, rect: byNode[current.nodeId] } : current,
        );
        setTileCue((current) =>
          current && byNode[current.nodeId] ? { ...current, rect: byNode[current.nodeId] } : current,
        );
      } else if (msg.type === DSI.HOVER) {
        const target = msg.payload as unknown as DsiTarget;
        if (isCandidate(target)) {
          onGameHover(target);
        } else {
          window.clearTimeout(hoverOutTimerRef.current);
          hoverOutTimerRef.current = undefined;
          setOverlay(null);
          clearHover();
          applyCapture(false);
        }
      } else if (msg.type === DSI.HOVER_OUT) {
        // The pointer left the game: for other markup, for another target, or by leaving the page. The
        // overlay goes with it — it takes no pointer events of its own, so nothing else would take it down.
        //
        // Capture is deliberately NOT dropped here. Turning it off costs two hops (the site posts the
        // next hover, we answer with set-capture, the site applies it) while the site reads the flag
        // synchronously on click, so a quick tile -> background -> tile -> click would land with capture
        // off and launch the game, losing the preview. It is dropped when a hover resolves to something
        // that is not a game (just below), which is the moment the owner is actually aiming at the site.
        const nodeId = typeof msg.payload.nodeId === 'string' ? msg.payload.nodeId : null;
        if (!nodeId || nodeId === hoverTargetRef.current) {
          // Deferred: moving to an adjacent tile of the same list emits hover-out then hover, and the
          // overlay is list-wide, so an immediate clear would blink it. onGameHover cancels this.
          window.clearTimeout(hoverOutTimerRef.current);
          hoverOutTimerRef.current = window.setTimeout(() => {
            setOverlay(null);
            clearHover();
          }, HOVER_OUT_GRACE_MS);
        }
      } else if (msg.type === DSI.CLICK) {
        // A game clicked: the site swallowed the click itself (capture), so nothing launched; open its list.
        const target = msg.payload as unknown as DsiTarget;
        if (!isCandidate(target)) return;
        void resolve(target).then((hit) => {
          if (!hit) return;
          setOverlay(null);
          clearHover();
          applyCapture(false);
          open(hit.dsId);
        });
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(hoverOutTimerRef.current);
      hoverOutTimerRef.current = undefined;
    };
  }, [origin, iframeRef, isCandidate, resolve, measureList, applyCapture, clearHover, open]);

  const pageReady = React.useCallback((): void => {
    instancesRef.current.clear();
    rectsRef.current.clear();
    pinnedRef.current.clear();
    sourcesRef.current = [];
    setOverlay(null);
    clearHover();
    // The bridge boots swallowing clicks on data-source elements; the owner should browse the site freely.
    captureOnRef.current = true;
    applyCapture(false);
  }, [applyCapture, clearHover]);

  // A page's lists load after it connects, so drafts are pinned as their lists turn up in its sources.
  const pinDrafts = React.useCallback(
    (sources: DsiSourceInfo[]): void => {
      if (sources.length) sourcesRef.current = sources;
      const onPage = new Set(sources.map((source) => source.id));
      for (const dsId of Object.keys(draftsRef.current)) {
        if (!onPage.has(dsId) || pinnedRef.current.has(dsId)) continue;
        pinnedRef.current.add(dsId);
        void fetchOriginal(dsId).then(() => applyDraft(dsId));
      }
    },
    [fetchOriginal, applyDraft],
  );

  const reorder = React.useCallback(
    (ids: string[]): void => {
      if (openId) onDraftChange(openId, ids);
    },
    [openId, onDraftChange],
  );

  const reset = React.useCallback((): void => {
    if (!openId) return;
    onDraftChange(openId, null);
    post(DSI.UNSET_SOURCE, { dsId: openId });
    pinnedRef.current.delete(openId);
  }, [openId, onDraftChange, post]);

  const close = React.useCallback((): void => {
    if (openId) applyDraft(openId);
    setOpenId(null);
  }, [openId, applyDraft]);

  const openContent = openId ? contents[openId] : undefined;
  const opened = React.useMemo((): OpenGameList | null => {
    if (!openId) return null;
    const draft = drafts[openId];
    const games = openContent ? gamesOf(openContent) : null;
    return {
      dsId: openId,
      games: games && draft ? orderedGames(games, draft) : games,
      failed: openFailed && !games,
      // The same comparison the tab makes, and for the same reason: a published override POSITIONS the
      // owner's games and leaves the rest in, so a game the platform adds afterwards lands after them.
      // Against strict equality that reads as unpublished changes, and re-publishing cannot clear it. The
      // tab moved to `orderHolds`; leaving this on `sameOrder` had the two surfaces disagreeing about
      // whether one list was dirty, with the preview offering a Publish that could never settle.
      dirty: Boolean(games && draft && !orderHolds(draft, games.map(gameKey))),
    };
  }, [openId, openContent, openFailed, drafts]);

  return {
    pageReady,
    pinDrafts,
    overlay,
    tileCue: tileCue ? { dsId: tileCue.dsId, rect: tileCue.rect } : null,
    opened,
    reorder,
    reset,
    close,
  };
};

export default usePreviewGameLists;
